import React, { ChangeEvent, useLayoutEffect, useState } from "react";
import Steps from "common/Steps/Steps.tsx";
import Modal from "common/Modal/Modal.tsx";
import Input from "common/Inputs/Input.tsx";
import styled from "styled-components";
import Text from "common/Typography/Text";
import { sharedLib } from "@shared/services/shared.service.ts";
import {
  copyCodeToClipboard,
  displayResultMessage,
  openMessage,
} from "utils/misc.tsx";
import Title from "common/Typography/Title.tsx";
import AnimatedCheckIcon from "common/Animations/AnimatedCheckIcon.tsx";
import { ExternalAccountInvitation } from "@shared/types";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils.ts";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import InputBox from "common/Inputs/InputBox.tsx";
import { getExternalInviteLink } from "features/configuration/common/utils.ts";

interface CreateInvitationModalProps {
  open?: boolean;
  onCancel?: () => void;
  invitation?: ExternalAccountInvitation;
}

const CreateInvitationModal = ({
  open,
  onCancel,
  invitation,
}: CreateInvitationModalProps) => {
  const [step, setStep] = useState(Step.CREATE);
  const [name, setName] = useState("");
  const [createAccountInvite, createAccountInviteStatus] =
    sharedLib.sharedApi.configuration.useCreateAccountInviteMutation();
  const isShareStep = step === Step.SHARE;

  useLayoutEffect(() => {
    if (invitation && !name) {
      setName(invitation.name);
    }
  }, [invitation]);

  const getCreateStep = () => {
    return (
      <>
        <InputTitle>Account Name</InputTitle>
        <Input
          value={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
        />
      </>
    );
  };

  const closeModal = () => {
    onCancel?.();
    setName("");
    setStep(Step.CREATE);
  };

  const handleCreateInvitation = () => {
    if (isShareStep) {
      closeModal();
    } else {
      displayResultMessage(createAccountInvite({ name }).unwrap())
        .then(() => setStep(Step.SHARE))
        .catch(() => {});
    }
  };

  const shareLink = getExternalInviteLink(
    createAccountInviteStatus.data?.token
  );

  const getShareStep = () => {
    return (
      <>
        <div style={{ textAlign: "center" }}>
          <AnimatedCheckIcon size={100} />
          <Title level={2} style={{ margin: "12px 0 28px 0" }}>
            Invitation {invitation ? "Updated" : "Created"} Successfully!
          </Title>
        </div>
        <InputBox
          style={{ marginBottom: 12 }}
          onClick={() => {
            copyCodeToClipboard(shareLink);
            openMessage(getCopyMessage("Invite link"), "info");
          }}
        >
          <Text
            type="secondary"
            $ellipsis={true}
            style={{
              flex: 1,
            }}
          >
            {shareLink}
          </Text>
          <CopyOutlined style={{ paddingLeft: 8 }} />
        </InputBox>
        <Text>
          Copy the link and send it to your users to invite them to connect
          their exchange account to your Cornix account.
        </Text>
      </>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      title={`${invitation ? "Update" : "Create"} Invitation`}
      okText={isShareStep ? undefined : invitation ? "Save" : "Create"}
      onOk={handleCreateInvitation}
      okButtonProps={{
        disabled: !name,
        loading: createAccountInviteStatus.isLoading,
      }}
      destroyOnHidden={true}
    >
      <Steps
        current={step}
        style={{ marginBottom: "24px" }}
        items={[
          {
            title: invitation ? "Update" : "Create",
          },
          {
            title: "Share",
          },
        ]}
      />
      {step === Step.CREATE && getCreateStep()}
      {step === Step.SHARE && getShareStep()}
    </Modal>
  );
};

export default CreateInvitationModal;

enum Step {
  CREATE,
  SHARE,
}

const InputTitle = styled(Text)`
  margin-bottom: 8px;
`;
